import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, RotateCcw, Globe, Share2, Type, DollarSign, Sparkles, ShoppingBag, Link2, Trash2, Plus, Ruler, Upload, Loader2, Home, X, MapPin, LayoutList, FileText, PartyPopper, Mail, ShieldCheck, Cookie, Truck } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import AdminBilingualInput from '@/components/admin/AdminBilingualInput';
import AdminConfirmDialog from '@/components/admin/AdminConfirmDialog';
import toast from 'react-hot-toast';
import { compressImage, formatBytes } from '@/lib/image-utils';
import type { SiteSettings, BilingualText, ShopSettings, ShareButtons } from '@/types';
import type { MenuKey } from '@/lib/menu-labels';
import { detectPlatform } from '@/components/SocialSidebar';

const INPUT_CLS = "w-full px-4 py-3 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20";
const LABEL_CLS = "block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2";

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <div className="flex items-center gap-2 mb-4 pt-2">
    <div className="w-8 h-8 rounded-lg bg-black/[0.03] dark:bg-white/[0.03] flex items-center justify-center">{icon}</div>
    <h3 className="text-[11px] font-bold uppercase tracking-[0.15em]">{title}</h3>
  </div>
);

const EMPTY_BIL: BilingualText = { en: '', ka: '' };

const MENU_NAME_CLS = "w-full px-3 py-2 bg-white dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-lg text-[11px] font-medium outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20";

/** Navigation entries an admin can rename and switch off, in the order they are listed. */
const MENU_ITEMS: Array<{ key: MenuKey; label: string }> = [
  { key: 'home', label: 'Home' },
  { key: 'shop', label: 'Shop' },
  { key: 'artists', label: 'Artists' },
  { key: 'exhibitions', label: 'Exhibitions' },
  { key: 'artFairs', label: 'Art Fairs' },
  { key: 'dropshop', label: 'Dropshop' },
  { key: 'news', label: 'News' },
  { key: 'contact', label: 'Contact' },
  { key: 'team', label: 'Team' },
  { key: 'faq', label: 'FAQ' },
  { key: 'partners', label: 'Partners' },
  { key: 'terms', label: 'Terms & Conditions' },
];

const AdminSettingsPage: React.FC = () => {
  const settings = useAdminStore((s) => s.settings);
  const updateSettings = useAdminStore((s) => s.updateSettings);
  const resetToMockData = useAdminStore((s) => s.resetToMockData);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Defensive: all nested accesses use optional chaining + fallbacks
  const s = settings ?? {} as SiteSettings;
  const [defaultCurrency, setDefaultCurrency] = useState<'USD' | 'GEL'>(s.defaultCurrency ?? 'USD');
  const [paymentEnabled, setPaymentEnabled] = useState(s.paymentEnabled ?? false);
  const [shopComingSoon, setShopComingSoon] = useState(s.shopComingSoon ?? true);
  const socialObj = s.socialLinks ?? { instagram: '', facebook: '', behance: '', pinterest: '' };
  const frame = s.frameMultipliers ?? { black: 1.0, white: 1.0, custom: 1.0 };
  const hero = s.heroContent ?? { title: 'FOTOATELIER', subtitle: EMPTY_BIL };
  const shop = s.shopSettings ?? { visibleFilters: { category: true, size: true, rarity: true, priceRange: true }, defaultSort: 'recommended' as const, defaultGridColumns: 3 as const };
  const filters = shop.visibleFilters ?? { category: true, size: true, rarity: true, priceRange: true };
  const banner = shop.saleBanner;
  const share = s.shareButtons;
  const limits = s.fieldLimits ?? { title: 80, shortDescription: 200, description: 2000, excerpt: 300 };

  const [galleryName, setGalleryName] = useState<BilingualText>(s.galleryName ?? EMPTY_BIL);
  const [galleryDescription, setGalleryDescription] = useState<BilingualText>(s.galleryDescription ?? EMPTY_BIL);
  // Convert old object format to array
  const initSocialLinks = (): string[] => {
    if (Array.isArray(s.socialLinksArray)) return s.socialLinksArray;
    const links: string[] = [];
    if (socialObj.instagram) links.push(socialObj.instagram);
    if (socialObj.facebook) links.push(socialObj.facebook);
    if (socialObj.behance) links.push(socialObj.behance);
    if (socialObj.pinterest) links.push(socialObj.pinterest);
    return links.length ? links : [''];
  };
  const [socialLinks, setSocialLinks] = useState<string[]>(initSocialLinks());
  const [footerText, setFooterText] = useState<BilingualText>(s.footerText ?? EMPTY_BIL);
  const [shippingThreshold, setShippingThreshold] = useState<number | string>(s.shippingThreshold ?? 50000);
  const [taxPercentage, setTaxPercentage] = useState<number | string>(s.taxPercentage ?? 0);
  // Stored as percentage (0 = no markup, 15 = +15%). Saved as multiplier (1.0, 1.15) for backward compat.
  const multToPercent = (m: number): number => m > 1 ? Math.round((m - 1) * 1000) / 10 : 0;
  const [blackPercent, setBlackPercent] = useState<number | string>(multToPercent(frame.black ?? 1.0));
  const [whitePercent, setWhitePercent] = useState<number | string>(multToPercent(frame.white ?? 1.0));
  const [customPercent, setCustomPercent] = useState<number | string>(multToPercent(frame.custom ?? 1.0));
  const [heroTitle, setHeroTitle] = useState(hero.title ?? 'FOTOATELIER');
  const [heroSubtitle, setHeroSubtitle] = useState<BilingualText>(hero.subtitle ?? EMPTY_BIL);

  // Shop settings
  const [filterCategory, setFilterCategory] = useState(filters.category ?? true);
  const [filterSize, setFilterSize] = useState(filters.size ?? true);
  const [filterRarity, setFilterRarity] = useState(filters.rarity ?? true);
  const [filterPrice, setFilterPrice] = useState(filters.priceRange ?? true);
  const [defaultSort, setDefaultSort] = useState(shop.defaultSort ?? 'recommended');
  const [defaultGrid, setDefaultGrid] = useState<2 | 3 | 4>(shop.defaultGridColumns ?? 3);
  const [saleBannerEnabled, setSaleBannerEnabled] = useState(banner?.enabled ?? false);
  const [saleBannerTextEn, setSaleBannerTextEn] = useState(banner?.text?.en ?? '');
  const [saleBannerTextKa, setSaleBannerTextKa] = useState(banner?.text?.ka ?? '');
  const [saleBannerBg, setSaleBannerBg] = useState(banner?.backgroundColor ?? '#000000');
  const [saleBannerColor, setSaleBannerColor] = useState(banner?.textColor ?? '#ffffff');

  // Share button visibility
  const [shareFacebook, setShareFacebook] = useState(share?.facebook ?? true);
  const [shareTwitter, setShareTwitter] = useState(share?.twitter ?? true);
  const [sharePinterest, setSharePinterest] = useState(share?.pinterest ?? true);
  const [shareWhatsapp, setShareWhatsapp] = useState(share?.whatsapp ?? true);
  const [shareTelegram, setShareTelegram] = useState(share?.telegram ?? true);
  const [shareCopyLink, setShareCopyLink] = useState(share?.copyLink ?? true);

  // Field limits
  const [limitTitle, setLimitTitle] = useState<number | string>(limits.title);
  const [limitShortDesc, setLimitShortDesc] = useState<number | string>(limits.shortDescription);
  const [limitDesc, setLimitDesc] = useState<number | string>(limits.description);
  const [limitExcerpt, setLimitExcerpt] = useState<number | string>(limits.excerpt);

  // Detail Page Tabs
  const dtabs = s.detailPageTabs ?? { details: true, artist: true, shipping: true, reviews: true };
  const [tabDetails, setTabDetails] = useState(dtabs.details);
  const [tabArtist, setTabArtist] = useState(dtabs.artist);
  const [tabShipping, setTabShipping] = useState(dtabs.shipping);
  const [tabReviews, setTabReviews] = useState(dtabs.reviews);

  // Payment providers (which gateways appear at checkout)
  const provs = s.paymentProviders ?? { bog: true, unipay: true };
  const [providerBog, setProviderBog] = useState(provs.bog !== false);
  const [providerUnipay, setProviderUnipay] = useState(provs.unipay !== false);

  // How a buyer may receive an order. Both off would leave checkout with nothing to pick,
  // so turning the last one off is refused rather than saved.
  const delivery = s.deliveryMethodsEnabled ?? { pickup: true, delivery: true };
  const [pickupOn, setPickupOn] = useState(delivery.pickup !== false);
  const [deliveryOn, setDeliveryOn] = useState(delivery.delivery !== false);

  // Frame Options (toggle which frame choices show on the artwork detail page)
  const framesEnabled = s.frameOptionsEnabled ?? { unframed: true, black: true, white: true, custom: true };
  const [frameUnframedOn, setFrameUnframedOn] = useState(framesEnabled.unframed !== false);
  const [frameBlackOn, setFrameBlackOn] = useState(framesEnabled.black !== false);
  const [frameWhiteOn, setFrameWhiteOn] = useState(framesEnabled.white !== false);
  const [frameCustomOn, setFrameCustomOn] = useState(framesEnabled.custom !== false);

  // Contact Info
  const contact = s.contactInfo ?? { address: EMPTY_BIL, email: '', phone: '', hours: EMPTY_BIL };
  const [contactAddress, setContactAddress] = useState<BilingualText>(contact.address ?? EMPTY_BIL);
  const [contactEmail, setContactEmail] = useState(contact.email ?? '');
  const [contactPhone, setContactPhone] = useState(contact.phone ?? '');
  const [contactHours, setContactHours] = useState<BilingualText>(contact.hours ?? EMPTY_BIL);

  // Menu Visibility
  const mv = s.menuVisibility ?? { home: true, shop: true, artists: true, exhibitions: true, artFairs: true, dropshop: true, news: true, contact: true, team: true, faq: true, partners: true, terms: true };
  const [menuVis, setMenuVis] = useState(mv);
  // Custom names for menu entries. An empty field means "use the built-in name".
  const [menuLabels, setMenuLabels] = useState<Partial<Record<MenuKey, BilingualText>>>(s.menuLabels ?? {});
  const setMenuName = (key: MenuKey, lang: 'en' | 'ka', value: string): void => {
    setMenuLabels((prev) => ({ ...prev, [key]: { ...(prev[key] ?? EMPTY_BIL), [lang]: value } }));
  };
  // Section visibility (toggle individual page sections)
  const sv = s.sectionVisibility ?? { aboutFooter: true, aboutValues: true, aboutStats: true, siteFooter: true, newsletter: true };
  const [sectionVis, setSectionVis] = useState(sv);

  // Home Collection Title
  const [homeCollectionTitle, setHomeCollectionTitle] = useState<BilingualText>(s.homeCollectionTitle ?? EMPTY_BIL);

  // Footer Section Titles
  const [footerAboutTitle, setFooterAboutTitle] = useState<BilingualText>(s.footerAboutTitle ?? EMPTY_BIL);
  const [footerLegalTitle, setFooterLegalTitle] = useState<BilingualText>(s.footerLegalTitle ?? EMPTY_BIL);

  // Newsletter Content
  const nl = s.newsletterContent ?? { label: EMPTY_BIL, title: EMPTY_BIL, subtitle: EMPTY_BIL };
  const [nlLabel, setNlLabel] = useState<BilingualText>(nl.label ?? EMPTY_BIL);
  const [nlTitle, setNlTitle] = useState<BilingualText>(nl.title ?? EMPTY_BIL);
  const [nlSubtitle, setNlSubtitle] = useState<BilingualText>(nl.subtitle ?? EMPTY_BIL);

  // Welcome Message
  const welcome = s.welcomeMessage ?? { title: EMPTY_BIL, text: EMPTY_BIL };
  const [welcomeTitle, setWelcomeTitle] = useState<BilingualText>(welcome.title ?? EMPTY_BIL);
  const [welcomeText, setWelcomeText] = useState<BilingualText>(welcome.text ?? EMPTY_BIL);

  // Home About Section
  const about = s.homeAboutSection ?? { label: EMPTY_BIL, title: EMPTY_BIL, description: EMPTY_BIL, image: '' };
  const [aboutLabel, setAboutLabel] = useState<BilingualText>(about.label);
  const [aboutTitle, setAboutTitle] = useState<BilingualText>(about.title);
  const [aboutDescription, setAboutDescription] = useState<BilingualText>(about.description);
  const [aboutImage, setAboutImage] = useState(about.image);
  const [aboutImageLoading, setAboutImageLoading] = useState(false);
  const aboutFileRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (Array.isArray(settings?.socialLinksArray)) {
      setSocialLinks(settings.socialLinksArray);
    } else {
      const sc2 = settings?.socialLinks ?? {};
      const arr = [sc2.instagram, sc2.facebook, sc2.behance, sc2.pinterest].filter(Boolean) as string[];
      setSocialLinks(arr.length ? arr : ['']);
    }
    const fr = settings?.frameMultipliers ?? { black: 1.0, white: 1.0, custom: 1.0 };
    const hr = settings?.heroContent ?? { title: 'FOTOATELIER', subtitle: EMPTY_BIL };
    const sh = settings?.shopSettings ?? { visibleFilters: { category: true, size: true, rarity: true, priceRange: true }, defaultSort: 'recommended' as const, defaultGridColumns: 3 as const };
    const fl = sh.visibleFilters ?? { category: true, size: true, rarity: true, priceRange: true };
    const bn = sh.saleBanner;
    const sb = settings?.shareButtons;

    setGalleryName(settings?.galleryName ?? EMPTY_BIL);
    setDefaultCurrency(settings?.defaultCurrency ?? 'USD');
    setPaymentEnabled(settings?.paymentEnabled ?? false);
    setShopComingSoon(settings?.shopComingSoon ?? true);
    setGalleryDescription(settings?.galleryDescription ?? EMPTY_BIL);
    setFooterText(settings?.footerText ?? EMPTY_BIL);
    setShippingThreshold(settings?.shippingThreshold ?? 50000);
    setTaxPercentage(settings?.taxPercentage ?? 0);
    setBlackPercent(multToPercent(fr.black ?? 1.0));
    setWhitePercent(multToPercent(fr.white ?? 1.0));
    setCustomPercent(multToPercent(fr.custom ?? 1.0));
    setHeroTitle(hr.title ?? 'FOTOATELIER');
    setHeroSubtitle(hr.subtitle ?? EMPTY_BIL);
    setFilterCategory(fl.category ?? true);
    setFilterSize(fl.size ?? true);
    setFilterRarity(fl.rarity ?? true);
    setFilterPrice(fl.priceRange ?? true);
    setDefaultSort(sh.defaultSort ?? 'recommended');
    setDefaultGrid(sh.defaultGridColumns ?? 3);
    setSaleBannerEnabled(bn?.enabled ?? false);
    setSaleBannerTextEn(bn?.text?.en ?? '');
    setSaleBannerTextKa(bn?.text?.ka ?? '');
    setSaleBannerBg(bn?.backgroundColor ?? '#000000');
    setSaleBannerColor(bn?.textColor ?? '#ffffff');
    setShareFacebook(sb?.facebook ?? true);
    setShareTwitter(sb?.twitter ?? true);
    setSharePinterest(sb?.pinterest ?? true);
    setShareWhatsapp(sb?.whatsapp ?? true);
    setShareTelegram(sb?.telegram ?? true);
    setShareCopyLink(sb?.copyLink ?? true);
    const lm = settings?.fieldLimits ?? { title: 80, shortDescription: 200, description: 2000, excerpt: 300 };
    setLimitTitle(lm.title);
    setLimitShortDesc(lm.shortDescription);
    setLimitDesc(lm.description);
    setLimitExcerpt(lm.excerpt);
    const ab = settings?.homeAboutSection ?? { label: EMPTY_BIL, title: EMPTY_BIL, description: EMPTY_BIL, image: '' };
    setAboutLabel(ab.label);
    setAboutTitle(ab.title);
    setAboutDescription(ab.description);
    setAboutImage(ab.image);
    const dt = settings?.detailPageTabs ?? { details: true, artist: true, shipping: true, reviews: true };
    setTabDetails(dt.details);
    setTabArtist(dt.artist);
    setTabShipping(dt.shipping);
    setTabReviews(dt.reviews);
    const pp = settings?.paymentProviders ?? { bog: true, unipay: true };
    setProviderBog(pp.bog !== false);
    setProviderUnipay(pp.unipay !== false);
    const fe = settings?.frameOptionsEnabled ?? { unframed: true, black: true, white: true, custom: true };
    setFrameUnframedOn(fe.unframed !== false);
    setFrameBlackOn(fe.black !== false);
    setFrameWhiteOn(fe.white !== false);
    setFrameCustomOn(fe.custom !== false);
    const ct = settings?.contactInfo ?? { address: EMPTY_BIL, email: '', phone: '', hours: EMPTY_BIL };
    setContactAddress(ct.address ?? EMPTY_BIL);
    setContactEmail(ct.email ?? '');
    setContactPhone(ct.phone ?? '');
    setContactHours(ct.hours ?? EMPTY_BIL);
    setMenuVis(settings?.menuVisibility ?? { home: true, shop: true, artists: true, exhibitions: true, artFairs: true, dropshop: true, news: true, contact: true, team: true, faq: true, partners: true, terms: true });
    const dm = settings?.deliveryMethodsEnabled ?? { pickup: true, delivery: true };
    setPickupOn(dm.pickup !== false);
    setDeliveryOn(dm.delivery !== false);
    setMenuLabels(settings?.menuLabels ?? {});
    setSectionVis(settings?.sectionVisibility ?? { aboutFooter: true, aboutValues: true, aboutStats: true, siteFooter: true, newsletter: true });
    setHomeCollectionTitle(settings?.homeCollectionTitle ?? EMPTY_BIL);
    setFooterAboutTitle(settings?.footerAboutTitle ?? EMPTY_BIL);
    setFooterLegalTitle(settings?.footerLegalTitle ?? EMPTY_BIL);
    const nlc = settings?.newsletterContent ?? { label: EMPTY_BIL, title: EMPTY_BIL, subtitle: EMPTY_BIL };
    setNlLabel(nlc.label ?? EMPTY_BIL);
    setNlTitle(nlc.title ?? EMPTY_BIL);
    setNlSubtitle(nlc.subtitle ?? EMPTY_BIL);
    const wm = settings?.welcomeMessage ?? { title: EMPTY_BIL, text: EMPTY_BIL };
    setWelcomeTitle(wm.title ?? EMPTY_BIL);
    setWelcomeText(wm.text ?? EMPTY_BIL);
  }, [settings]);

  const handleSave = (): void => {
    const shopSettings: ShopSettings = {
      visibleFilters: { category: filterCategory, size: filterSize, rarity: filterRarity, priceRange: filterPrice },
      defaultSort,
      defaultGridColumns: defaultGrid,
      saleBanner: {
        enabled: saleBannerEnabled,
        text: { en: saleBannerTextEn, ka: saleBannerTextKa || saleBannerTextEn },
        backgroundColor: saleBannerBg,
        textColor: saleBannerColor,
      },
    };
    const shareButtonsConfig: ShareButtons = {
      facebook: shareFacebook,
      twitter: shareTwitter,
      pinterest: sharePinterest,
      whatsapp: shareWhatsapp,
      telegram: shareTelegram,
      copyLink: shareCopyLink,
    };
    const updated: SiteSettings = {
      galleryName, galleryDescription, defaultCurrency, paymentEnabled, shopComingSoon,
      socialLinksArray: socialLinks.filter((l) => l.trim()),
      socialLinks: {
        instagram: socialLinks.find((l) => l.includes('instagram')) || '',
        facebook: socialLinks.find((l) => l.includes('facebook')) || '',
        behance: socialLinks.find((l) => l.includes('behance')) || '',
        pinterest: socialLinks.find((l) => l.includes('pinterest')) || '',
      },
      footerText, shippingThreshold: Number(shippingThreshold || 0), taxPercentage: Number(taxPercentage || 0),
      frameMultipliers: {
        black: 1 + (Number(blackPercent || 0) / 100),
        white: 1 + (Number(whitePercent || 0) / 100),
        custom: 1 + (Number(customPercent || 0) / 100),
      },
      heroContent: { title: heroTitle, subtitle: heroSubtitle },
      homeAboutSection: { label: aboutLabel, title: aboutTitle, description: aboutDescription, image: aboutImage },
      shareButtons: shareButtonsConfig,
      shopSettings,
      fieldLimits: {
        title: Number(limitTitle) || 80,
        shortDescription: Number(limitShortDesc) || 200,
        description: Number(limitDesc) || 2000,
        excerpt: Number(limitExcerpt) || 300,
      },
      detailPageTabs: {
        details: tabDetails,
        artist: tabArtist,
        shipping: tabShipping,
        reviews: tabReviews,
      },
      frameOptionsEnabled: {
        unframed: frameUnframedOn,
        black: frameBlackOn,
        white: frameWhiteOn,
        custom: frameCustomOn,
      },
      paymentProviders: {
        bog: providerBog,
        unipay: providerUnipay,
      },
      contactInfo: {
        address: contactAddress,
        email: contactEmail,
        phone: contactPhone,
        hours: contactHours,
      },
      menuVisibility: menuVis,
      menuLabels,
      deliveryMethodsEnabled: { pickup: pickupOn, delivery: deliveryOn },
      sectionVisibility: sectionVis,
      homeCollectionTitle,
      footerAboutTitle,
      footerLegalTitle,
      newsletterContent: { label: nlLabel, title: nlTitle, subtitle: nlSubtitle },
      welcomeMessage: { title: welcomeTitle, text: welcomeText },
      termsContent: settings?.termsContent,
      privacyContent: settings?.privacyContent,
      cookieContent: settings?.cookieContent,
      careersContent: settings?.careersContent,
      shippingContent: settings?.shippingContent,
      aboutPageContent: settings?.aboutPageContent,
    };
    updateSettings(updated);
    toast.success('Settings saved');
  };

  const handleReset = (): void => {
    resetToMockData();
    toast.success('All data reset to defaults');
    setShowResetConfirm(false);
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-syncopate font-bold tracking-tighter">Settings</h1>
        <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">Site configuration</p>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-6 space-y-6">
        <div>
          <SectionHeader icon={<Globe size={14} className="text-black/40 dark:text-white/40" />} title="Gallery Information" />
          <div className="space-y-4">
            <AdminBilingualInput label="Gallery Name" value={galleryName} onChange={setGalleryName} placeholder="Gallery name" />
            <div>
              <label className={LABEL_CLS}>Default Currency</label>
              <div className="flex bg-black/[0.04] rounded-xl p-1 w-fit">
                <button
                  onClick={() => setDefaultCurrency('USD')}
                  className={`px-5 py-2.5 text-[11px] font-bold uppercase tracking-wider rounded-lg transition-all ${
                    defaultCurrency === 'USD'
                      ? 'bg-black text-white shadow-sm'
                      : 'text-black/40 hover:text-black/60'
                  }`}
                >
                  $ USD
                </button>
                <button
                  onClick={() => setDefaultCurrency('GEL')}
                  className={`px-5 py-2.5 text-[11px] font-bold uppercase tracking-wider rounded-lg transition-all ${
                    defaultCurrency === 'GEL'
                      ? 'bg-black text-white shadow-sm'
                      : 'text-black/40 hover:text-black/60'
                  }`}
                >
                  ₾ GEL
                </button>
              </div>
              <p className="text-[9px] text-black/25 mt-1.5">Sets the default currency shown to new visitors</p>
            </div>

            {/* Payment Toggle */}
            <div className="flex items-center justify-between p-4 bg-black/[0.02] rounded-xl">
              <div>
                <label className={LABEL_CLS}>Online Payments</label>
                <p className="text-[9px] text-black/25 -mt-1">Master switch — turns all online payment processing on/off</p>
              </div>
              <button
                onClick={() => setPaymentEnabled(!paymentEnabled)}
                className={`relative w-12 h-6 rounded-full transition-all ${paymentEnabled ? 'bg-green-500' : 'bg-black/[0.1]'}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${paymentEnabled ? 'left-[26px]' : 'left-0.5'}`} />
              </button>
            </div>

            {/* Payment Providers */}
            <div className="p-4 bg-black/[0.02] rounded-xl space-y-1">
              <label className={LABEL_CLS}>Payment Providers</label>
              <p className="text-[9px] text-black/25 mb-2">
                Which gateways the customer can choose at checkout. A provider only appears if its API credentials are also set on the server.
              </p>
              {[
                { key: 'bog', label: 'Bank of Georgia', hint: 'საქართველოს ბანკი', state: providerBog, set: setProviderBog },
                { key: 'unipay', label: 'UniPay', hint: 'UniPay gateway', state: providerUnipay, set: setProviderUnipay },
              ].map((p) => (
                <div key={p.key} className="flex items-center justify-between py-1.5">
                  <div>
                    <span className="text-[11px] font-medium text-black/70">{p.label}</span>
                    <span className="text-[9px] text-black/25 ml-2">{p.hint}</span>
                  </div>
                  <button
                    onClick={() => p.set(!p.state)}
                    className={`relative w-9 h-5 rounded-full transition-all ${p.state ? 'bg-black' : 'bg-black/[0.08]'}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${p.state ? 'left-[18px]' : 'left-0.5'}`} />
                  </button>
                </div>
              ))}
            </div>

            {/* How the buyer receives the order */}
            <div className="p-4 bg-black/[0.02] rounded-xl space-y-1">
              <label className={LABEL_CLS}>Delivery Options</label>
              <p className="text-[9px] text-black/25 mb-2">
                Which options the buyer can choose at checkout. At least one has to stay on.
              </p>
              {[
                { key: 'pickup', label: 'Pick up from gallery', hint: 'თვითგატანა გალერეიდან', state: pickupOn, set: setPickupOn, other: deliveryOn },
                { key: 'delivery', label: 'Delivery', hint: 'მიტანა — Georgian Post', state: deliveryOn, set: setDeliveryOn, other: pickupOn },
              ].map((d) => (
                <div key={d.key} className="flex items-center justify-between py-1.5">
                  <div>
                    <span className="text-[11px] font-medium text-black/70">{d.label}</span>
                    <span className="text-[9px] text-black/25 ml-2">{d.hint}</span>
                  </div>
                  <button
                    type="button"
                    aria-label={`${d.label} ${d.state ? 'on' : 'off'}`}
                    onClick={() => {
                      if (d.state && !d.other) {
                        toast.error('Keep at least one delivery option on — otherwise nobody can check out.');
                        return;
                      }
                      d.set(!d.state);
                    }}
                    className={`relative w-9 h-5 rounded-full transition-all ${d.state ? 'bg-black' : 'bg-black/[0.08]'}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${d.state ? 'left-[18px]' : 'left-0.5'}`} />
                  </button>
                </div>
              ))}
            </div>

            {/* Shop Coming Soon Toggle */}
            <div className="flex items-center justify-between p-4 bg-black/[0.02] rounded-xl">
              <div>
                <label className={LABEL_CLS}>Shop "Coming Soon" Mode</label>
                <p className="text-[9px] text-black/25 -mt-1">Hide shop & artwork pages, show "Coming Soon" instead</p>
              </div>
              <button
                onClick={() => setShopComingSoon(!shopComingSoon)}
                className={`relative w-12 h-6 rounded-full transition-all ${shopComingSoon ? 'bg-amber-500' : 'bg-black/[0.1]'}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${shopComingSoon ? 'left-[26px]' : 'left-0.5'}`} />
              </button>
            </div>
          </div>
        </div>

        <div className="border-t border-black/[0.04]" />

        <div>
          <SectionHeader icon={<MapPin size={14} className="text-black/40" />} title="Contact Information" />
          <div className="space-y-4">
            <AdminBilingualInput label="Address" value={contactAddress} onChange={setContactAddress} placeholder="e.g. 12 Rustaveli Avenue, Tbilisi" />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={LABEL_CLS}>Email</label>
                <input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="info@example.com" className={INPUT_CLS} />
              </div>
              <div>
                <label className={LABEL_CLS}>Phone</label>
                <input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="+995 32 123 4567" className={INPUT_CLS} />
              </div>
            </div>
            <AdminBilingualInput label="Working Hours" value={contactHours} onChange={setContactHours} placeholder="e.g. Tue – Sun, 11:00 – 19:00" />
          </div>
        </div>

        <div className="border-t border-black/[0.04]" />

        <div>
          <SectionHeader icon={<Share2 size={14} className="text-black/40" />} title="Social Links" />
          <p className="text-[9px] text-black/25 dark:text-white/25 mb-3 -mt-2">Paste any social media URL — the platform icon is auto-detected</p>
          <div className="space-y-3">
            {socialLinks.map((link, idx) => {
              const detected = link.trim() ? detectPlatform(link) : null;
              return (
                <div key={idx} className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-black/[0.04] dark:bg-white/[0.06] flex items-center justify-center shrink-0">
                    {detected ? (
                      <span className="w-4 h-4 [&>svg]:w-4 [&>svg]:h-4 text-black/50 dark:text-white/50">{detected.icon}</span>
                    ) : (
                      <Link2 size={14} className="text-black/20 dark:text-white/20" />
                    )}
                  </div>
                  <div className="flex-1 relative">
                    <input
                      value={link}
                      onChange={(e) => { const u = [...socialLinks]; u[idx] = e.target.value; setSocialLinks(u); }}
                      placeholder="https://..."
                      className={INPUT_CLS}
                    />
                    {detected && (
                      <span className="absolute right-10 top-1/2 -translate-y-1/2 text-[8px] font-bold text-black/30 dark:text-white/30 uppercase tracking-wider">{detected.name}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const u = socialLinks.filter((_, i) => i !== idx);
                      setSocialLinks(u.length ? u : ['']);
                    }}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-black/20 dark:text-white/20 hover:text-red-500 hover:bg-red-500/5 transition-all shrink-0"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              );
            })}
            <button
              onClick={() => setSocialLinks([...socialLinks, ''])}
              className="flex items-center gap-2 px-4 py-2.5 text-[9px] font-bold uppercase tracking-wider bg-black/[0.04] hover:bg-black/[0.08] rounded-xl transition-all w-full justify-center"
            >
              <Plus size={13} /> Add Social Link
            </button>
          </div>
        </div>

        <div className="border-t border-black/[0.04] dark:border-white/[0.04]" />

        <div>
          <SectionHeader icon={<Link2 size={14} className="text-black/40 dark:text-white/40" />} title="Share Buttons (Product Page)" />
          <p className="text-[9px] text-black/25 dark:text-white/25 mb-3 -mt-2">Control which social share buttons appear on artwork detail pages</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { label: 'Facebook', value: shareFacebook, setter: setShareFacebook },
              { label: 'X (Twitter)', value: shareTwitter, setter: setShareTwitter },
              { label: 'Pinterest', value: sharePinterest, setter: setSharePinterest },
              { label: 'WhatsApp', value: shareWhatsapp, setter: setShareWhatsapp },
              { label: 'Telegram', value: shareTelegram, setter: setShareTelegram },
              { label: 'Copy Link', value: shareCopyLink, setter: setShareCopyLink },
            ].map((s) => (
              <div key={s.label} className="flex items-center justify-between px-4 py-3 bg-black/[0.02] dark:bg-white/[0.02] rounded-xl">
                <span className="text-[11px] font-medium">{s.label}</span>
                <button
                  type="button"
                  onClick={() => s.setter(!s.value)}
                  className={`relative w-10 rounded-full transition-all ${s.value ? 'bg-black dark:bg-white' : 'bg-black/[0.08] dark:bg-white/[0.1]'}`}
                  style={{ height: 22 }}
                >
                  <span className={`absolute top-0.5 rounded-full transition-all ${s.value ? 'left-[18px] bg-white dark:bg-black' : 'left-0.5 bg-white dark:bg-black'}`} style={{ width: 18, height: 18 }} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-black/[0.04] dark:border-white/[0.04]" />

        <div>
          <SectionHeader icon={<DollarSign size={14} className="text-black/40 dark:text-white/40" />} title="Pricing" />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLS}>Free Shipping Threshold (cents)</label>
              <input type="number" value={shippingThreshold} onChange={(e) => setShippingThreshold(e.target.value === '' ? '' : Number(e.target.value))} className={INPUT_CLS} />
              <p className="text-[9px] text-black/25 dark:text-white/25 mt-1">Currently: {(Number(shippingThreshold || 0) / 100).toLocaleString()} USD</p>
            </div>
            <div>
              <label className={LABEL_CLS}>Tax Percentage (%)</label>
              <input type="number" value={taxPercentage} onChange={(e) => setTaxPercentage(e.target.value === '' ? '' : Number(e.target.value))} step="0.1" className={INPUT_CLS} />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-[9px] text-black/40 dark:text-white/40 mb-2">Frame price markup as percentage: <strong>0</strong> = no markup, <strong>15</strong> = +15%, <strong>20</strong> = +20%</p>
            <div className="grid grid-cols-3 gap-4">
              <div><label className={LABEL_CLS}>Black Frame Markup (%)</label><input type="number" value={blackPercent} onChange={(e) => setBlackPercent(e.target.value === '' ? '' : Number(e.target.value))} step="0.1" min="0" max="500" className={INPUT_CLS} /></div>
              <div><label className={LABEL_CLS}>White Frame Markup (%)</label><input type="number" value={whitePercent} onChange={(e) => setWhitePercent(e.target.value === '' ? '' : Number(e.target.value))} step="0.1" min="0" max="500" className={INPUT_CLS} /></div>
              <div><label className={LABEL_CLS}>Custom Frame Markup (%)</label><input type="number" value={customPercent} onChange={(e) => setCustomPercent(e.target.value === '' ? '' : Number(e.target.value))} step="0.1" min="0" max="500" className={INPUT_CLS} /></div>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-black/[0.04] dark:border-white/[0.04]">
            <p className="text-[10px] font-bold uppercase tracking-widest text-black/50 dark:text-white/50 mb-1">Frame Options Visibility</p>
            <p className="text-[9px] text-black/40 dark:text-white/40 mb-3">Toggle which frame choices appear on the artwork detail page. If all are off, the frame selector is hidden.</p>
            <div className="space-y-2">
              {[
                { key: 'unframed' as const, label: 'Unframed', state: frameUnframedOn, set: setFrameUnframedOn },
                { key: 'black' as const, label: 'Black Frame', state: frameBlackOn, set: setFrameBlackOn },
                { key: 'white' as const, label: 'White Frame', state: frameWhiteOn, set: setFrameWhiteOn },
                { key: 'custom' as const, label: 'Custom Frame (Contact Us)', state: frameCustomOn, set: setFrameCustomOn },
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between py-1.5">
                  <span className="text-[11px] font-medium text-black/70 dark:text-white/70">{item.label}</span>
                  <button
                    onClick={() => item.set(!item.state)}
                    className={`relative w-9 h-5 rounded-full transition-all ${item.state ? 'bg-black dark:bg-white' : 'bg-black/[0.08] dark:bg-white/[0.08]'}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white dark:bg-black transition-all ${item.state ? 'left-[18px]' : 'left-0.5'}`} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-black/[0.04] dark:border-white/[0.04]" />

        <div>
          <SectionHeader icon={<Sparkles size={14} className="text-black/40 dark:text-white/40" />} title="Hero Content" />
          <div className="space-y-4">
            <div><label className={LABEL_CLS}>Hero Title</label><input value={heroTitle} onChange={(e) => setHeroTitle(e.target.value)} placeholder="e.g. FOTOATELIER" className={INPUT_CLS} /></div>
            <AdminBilingualInput label="Hero Subtitle" value={heroSubtitle} onChange={setHeroSubtitle} placeholder="Gallery tagline" />
          </div>
        </div>

        <div className="border-t border-black/[0.04] dark:border-white/[0.04]" />

        <div>
          <SectionHeader icon={<Home size={14} className="text-black/40 dark:text-white/40" />} title="Home — Collection Section" />
          <div className="space-y-4 mb-6">
            <AdminBilingualInput label="Collection Title" value={homeCollectionTitle} onChange={setHomeCollectionTitle} placeholder="e.g. COLLECTION / კოლექცია" />
          </div>

          <div className="border-t border-black/[0.04] dark:border-white/[0.04] pt-4" />

          <SectionHeader icon={<Home size={14} className="text-black/40 dark:text-white/40" />} title="Home — About Section" />
          <p className="text-[9px] text-black/25 dark:text-white/25 mb-3 -mt-2">The &ldquo;Fine Art Photography&rdquo; block on the homepage</p>
          <div className="space-y-4">
            <AdminBilingualInput label="Label (small text)" value={aboutLabel} onChange={setAboutLabel} placeholder="e.g. Gallery Fotoatelier" />
            <AdminBilingualInput label="Title" value={aboutTitle} onChange={setAboutTitle} placeholder="e.g. FINE ART PHOTOGRAPHY" />
            <AdminBilingualInput label="Description" value={aboutDescription} onChange={setAboutDescription} textarea placeholder="Gallery description paragraph" />

            {/* Image upload */}
            <div>
              <label className={LABEL_CLS}>Section Image</label>
              <div className="flex items-start gap-4">
                <div
                  onClick={() => !aboutImageLoading && aboutFileRef.current?.click()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const f = e.dataTransfer.files[0];
                    if (f) {
                      setAboutImageLoading(true);
                      compressImage(f).then(({ dataUrl, originalSize, compressedSize }) => {
                        setAboutImage(dataUrl);
                        toast.success(`Compressed: ${formatBytes(originalSize)} → ${formatBytes(compressedSize)}`);
                      }).catch(() => toast.error('Failed to process image')).finally(() => setAboutImageLoading(false));
                    }
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  className="w-28 h-28 rounded-xl bg-black/[0.03] dark:bg-white/[0.05] border border-dashed border-black/[0.1] dark:border-white/[0.1] flex-shrink-0 overflow-hidden flex items-center justify-center cursor-pointer hover:border-black/20 dark:hover:border-white/20 transition-all"
                >
                  {aboutImageLoading ? (
                    <Loader2 size={18} className="text-black/25 dark:text-white/25 animate-spin" />
                  ) : aboutImage ? (
                    <img src={aboutImage} alt="About section" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center">
                      <Upload size={18} className="text-black/15 dark:text-white/15 mx-auto mb-1" />
                      <span className="text-[8px] font-bold uppercase tracking-wider text-black/20 dark:text-white/20">Upload</span>
                    </div>
                  )}
                  <input
                    ref={aboutFileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) {
                        setAboutImageLoading(true);
                        compressImage(f).then(({ dataUrl, originalSize, compressedSize }) => {
                          setAboutImage(dataUrl);
                          toast.success(`Compressed: ${formatBytes(originalSize)} → ${formatBytes(compressedSize)}`);
                        }).catch(() => toast.error('Failed to process image')).finally(() => setAboutImageLoading(false));
                      }
                      e.target.value = '';
                    }}
                  />
                </div>
                <div className="flex-1 space-y-2">
                  {aboutImage ? (
                    <div className="flex items-center gap-2">
                      <span className={INPUT_CLS + ' flex-1 text-black/40 dark:text-white/40 text-[11px] truncate'}>
                        {aboutImage.startsWith('data:') ? '(uploaded file)' : aboutImage}
                      </span>
                      <button
                        type="button"
                        onClick={() => setAboutImage('')}
                        className="px-3 py-2 text-[8px] font-bold uppercase tracking-wider text-red-500 hover:bg-red-500/5 rounded-lg transition-all shrink-0"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={aboutImage}
                      onChange={(e) => setAboutImage(e.target.value)}
                      placeholder="https://... or click to upload"
                      className={INPUT_CLS}
                    />
                  )}
                  <p className="text-[8px] text-black/20 dark:text-white/20">Click or drag & drop an image. If empty, a decorative animation will be shown.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-black/[0.04] dark:border-white/[0.04]" />

        <div>
          <SectionHeader icon={<Ruler size={14} className="text-black/40 dark:text-white/40" />} title="Field Character Limits" />
          <p className="text-[9px] text-black/25 dark:text-white/25 mb-3 -mt-2">Control maximum character counts for text fields across all admin editors</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <label className={LABEL_CLS}>Title</label>
              <input type="number" value={limitTitle} onChange={(e) => setLimitTitle(e.target.value === '' ? '' : Number(e.target.value))} min={10} max={500} className={INPUT_CLS} />
              <p className="text-[8px] text-black/20 dark:text-white/20 mt-1">Artwork, artist, exhibition names</p>
            </div>
            <div>
              <label className={LABEL_CLS}>Short Description</label>
              <input type="number" value={limitShortDesc} onChange={(e) => setLimitShortDesc(e.target.value === '' ? '' : Number(e.target.value))} min={50} max={1000} className={INPUT_CLS} />
              <p className="text-[8px] text-black/20 dark:text-white/20 mt-1">Preview texts, short bios</p>
            </div>
            <div>
              <label className={LABEL_CLS}>Description</label>
              <input type="number" value={limitDesc} onChange={(e) => setLimitDesc(e.target.value === '' ? '' : Number(e.target.value))} min={100} max={10000} className={INPUT_CLS} />
              <p className="text-[8px] text-black/20 dark:text-white/20 mt-1">Full descriptions, bios, content</p>
            </div>
            <div>
              <label className={LABEL_CLS}>Excerpt</label>
              <input type="number" value={limitExcerpt} onChange={(e) => setLimitExcerpt(e.target.value === '' ? '' : Number(e.target.value))} min={50} max={1000} className={INPUT_CLS} />
              <p className="text-[8px] text-black/20 dark:text-white/20 mt-1">News excerpts, summaries</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Footer */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }} className="bg-white border border-black/[0.04] rounded-2xl p-6 space-y-6">
        <SectionHeader icon={<Type size={14} className="text-black/40" />} title="Footer" />
        <p className="text-[9px] text-black/25 -mt-3 mb-2">Footer section titles and subtitle text</p>
        <div className="space-y-4">
          <AdminBilingualInput label="Footer Subtitle (under logo)" value={footerText} onChange={setFooterText} placeholder="e.g. Fotoatelier, Tbilisi" />
          <AdminBilingualInput label="About Us Column Title" value={footerAboutTitle} onChange={setFooterAboutTitle} placeholder="e.g. About Us / ჩვენს შესახებ" />
          <AdminBilingualInput label="Legal Column Title" value={footerLegalTitle} onChange={setFooterLegalTitle} placeholder="e.g. Legal / სამართლებრივი" />
        </div>
      </motion.div>

      {/* Menu Visibility */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1, ease: [0.22, 1, 0.36, 1] }} className="bg-white border border-black/[0.04] rounded-2xl p-6 space-y-6">
        <SectionHeader icon={<LayoutList size={14} className="text-black/40" />} title="Menu Names & Visibility" />
        <p className="text-[9px] text-black/25 -mt-3 mb-2">Rename each entry and choose whether it appears in the site navigation. Leave a name blank to keep the built-in one. Switching an entry off closes its page — visitors who open the link see &ldquo;Coming Soon&rdquo;.</p>
        <div className="space-y-2">
          {MENU_ITEMS.map((item) => {
            const custom = menuLabels[item.key] ?? EMPTY_BIL;
            const on = menuVis[item.key] !== false;
            return (
              <div key={item.key} className="px-4 py-3 bg-black/[0.02] dark:bg-white/[0.03] rounded-xl">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[11px] font-medium">{item.label}</span>
                  <button
                    type="button"
                    aria-label={`${item.label} ${on ? 'visible' : 'hidden'}`}
                    onClick={() => setMenuVis((prev) => ({ ...prev, [item.key]: !on }))}
                    className={`relative shrink-0 w-9 h-5 rounded-full transition-all ${on ? 'bg-black' : 'bg-black/[0.08]'}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${on ? 'left-[18px]' : 'left-0.5'}`} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <input
                    value={custom.en ?? ''}
                    onChange={(e) => setMenuName(item.key, 'en', e.target.value)}
                    placeholder={`${item.label} (EN)`}
                    className={MENU_NAME_CLS}
                  />
                  <input
                    value={custom.ka ?? ''}
                    onChange={(e) => setMenuName(item.key, 'ka', e.target.value)}
                    placeholder={`${item.label} (KA)`}
                    className={MENU_NAME_CLS}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Section Visibility */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1, ease: [0.22, 1, 0.36, 1] }} className="bg-white border border-black/[0.04] rounded-2xl p-6 space-y-6">
        <SectionHeader icon={<LayoutList size={14} className="text-black/40" />} title="Page Sections Visibility" />
        <p className="text-[9px] text-black/25 -mt-3 mb-2">Toggle individual sections on the About page</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {([
            { key: 'siteFooter' as const, label: 'Site Footer (whole site)' },
            { key: 'newsletter' as const, label: 'Newsletter Section' },
            { key: 'aboutFooter' as const, label: 'About — Footer/CTA' },
            { key: 'aboutValues' as const, label: 'About — Values' },
            { key: 'aboutStats' as const, label: 'About — Statistics' },
          ]).map((item) => (
            <div key={item.key} className="flex items-center justify-between px-4 py-3 bg-black/[0.02] rounded-xl">
              <span className="text-[11px] font-medium">{item.label}</span>
              <button
                type="button"
                onClick={() => setSectionVis((prev) => ({ ...prev, [item.key]: !prev[item.key] }))}
                className={`relative w-9 h-5 rounded-full transition-all ${sectionVis[item.key] ? 'bg-black' : 'bg-black/[0.08]'}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${sectionVis[item.key] ? 'left-[18px]' : 'left-0.5'}`} />
              </button>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Shop Configuration */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1, ease: [0.22, 1, 0.36, 1] }} className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-6 space-y-6">
        <SectionHeader icon={<ShoppingBag size={14} className="text-black/40 dark:text-white/40" />} title="Shop Configuration" />

        {/* Filter Visibility */}
        <div>
          <label className={LABEL_CLS}>Visible Filters</label>
          <p className="text-[9px] text-black/25 dark:text-white/25 mb-3 -mt-1">Toggle which filter sections appear in the shop sidebar</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Category', value: filterCategory, setter: setFilterCategory },
              { label: 'Size', value: filterSize, setter: setFilterSize },
              { label: 'Rarity', value: filterRarity, setter: setFilterRarity },
              { label: 'Price Range', value: filterPrice, setter: setFilterPrice },
            ].map((f) => (
              <div key={f.label} className="flex items-center justify-between px-4 py-3 bg-black/[0.02] dark:bg-white/[0.02] rounded-xl">
                <span className="text-[11px] font-medium">{f.label}</span>
                <button
                  type="button"
                  onClick={() => f.setter(!f.value)}
                  className={`relative w-10 h-5.5 rounded-full transition-all ${f.value ? 'bg-black dark:bg-white' : 'bg-black/[0.08] dark:bg-white/[0.1]'}`}
                >
                  <span className={`absolute top-0.5 w-4.5 h-4.5 rounded-full transition-all ${f.value ? 'left-[18px] bg-white dark:bg-black' : 'left-0.5 bg-white dark:bg-black'}`} style={{ width: 18, height: 18 }} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-black/[0.04] dark:border-white/[0.04]" />

        {/* Default Sort & Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLS}>Default Sort</label>
            <select value={defaultSort} onChange={(e) => setDefaultSort(e.target.value as ShopSettings['defaultSort'])} className={INPUT_CLS}>
              <option value="recommended">Recommended</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="newest">Newest First</option>
              <option value="popular">Most Popular</option>
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Default Grid Columns</label>
            <div className="flex items-center gap-2 mt-1">
              {([2, 3, 4] as const).map((cols) => (
                <button
                  key={cols}
                  type="button"
                  onClick={() => setDefaultGrid(cols)}
                  className={`flex-1 py-3 text-[11px] font-bold rounded-xl transition-all ${
                    defaultGrid === cols
                      ? 'bg-black dark:bg-white text-white dark:text-black'
                      : 'bg-black/[0.03] dark:bg-white/[0.05] text-black/40 dark:text-white/40 hover:bg-black/[0.06] dark:hover:bg-white/[0.08]'
                  }`}
                >
                  {cols}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-black/[0.04] dark:border-white/[0.04]" />

        {/* Sale Banner */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className={LABEL_CLS + ' mb-0'}>Sale Banner</label>
            <button
              type="button"
              onClick={() => setSaleBannerEnabled(!saleBannerEnabled)}
              className={`relative w-10 rounded-full transition-all ${saleBannerEnabled ? 'bg-black dark:bg-white' : 'bg-black/[0.08] dark:bg-white/[0.1]'}`}
              style={{ height: 22 }}
            >
              <span className={`absolute top-0.5 rounded-full transition-all ${saleBannerEnabled ? 'left-[18px] bg-white dark:bg-black' : 'left-0.5 bg-white dark:bg-black'}`} style={{ width: 18, height: 18 }} />
            </button>
          </div>
          {saleBannerEnabled && (
            <div className="space-y-3 p-4 bg-black/[0.02] dark:bg-white/[0.02] rounded-xl">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL_CLS}>Banner Text (English)</label>
                  <input value={saleBannerTextEn} onChange={(e) => setSaleBannerTextEn(e.target.value)} placeholder="e.g. Summer Sale — 20% Off!" className={INPUT_CLS} />
                </div>
                <div>
                  <label className={LABEL_CLS}>Banner Text (Georgian)</label>
                  <input value={saleBannerTextKa} onChange={(e) => setSaleBannerTextKa(e.target.value)} placeholder="e.g. ზაფხულის ფასდაკლება" className={INPUT_CLS} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL_CLS}>Background Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={saleBannerBg} onChange={(e) => setSaleBannerBg(e.target.value)} className="w-10 h-10 rounded-lg border border-black/10 dark:border-white/10 cursor-pointer" />
                    <input value={saleBannerBg} onChange={(e) => setSaleBannerBg(e.target.value)} className={INPUT_CLS} />
                  </div>
                </div>
                <div>
                  <label className={LABEL_CLS}>Text Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={saleBannerColor} onChange={(e) => setSaleBannerColor(e.target.value)} className="w-10 h-10 rounded-lg border border-black/10 dark:border-white/10 cursor-pointer" />
                    <input value={saleBannerColor} onChange={(e) => setSaleBannerColor(e.target.value)} className={INPUT_CLS} />
                  </div>
                </div>
              </div>
              {/* Preview */}
              <div className="mt-2">
                <label className={LABEL_CLS}>Preview</label>
                <div className="py-2.5 px-4 rounded-xl text-center text-[11px] font-bold uppercase tracking-[0.15em]" style={{ backgroundColor: saleBannerBg, color: saleBannerColor }}>
                  {saleBannerTextEn || 'Sale Banner Preview'}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-black/[0.04] dark:border-white/[0.04]" />

        {/* Detail Page Tabs */}
        <div>
          <label className={LABEL_CLS}>Artwork Detail Page Tabs</label>
          <p className="text-[9px] text-black/25 dark:text-white/25 mb-3 -mt-1">Toggle which tabs appear on the artwork detail page</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'About the Work', value: tabDetails, setter: setTabDetails },
              { label: 'About the Artist', value: tabArtist, setter: setTabArtist },
              { label: 'Shipping', value: tabShipping, setter: setTabShipping },
              { label: 'Reviews', value: tabReviews, setter: setTabReviews },
            ].map((f) => (
              <div key={f.label} className="flex items-center justify-between px-4 py-3 bg-black/[0.02] dark:bg-white/[0.02] rounded-xl">
                <span className="text-[11px] font-medium">{f.label}</span>
                <button
                  type="button"
                  onClick={() => f.setter(!f.value)}
                  className={`relative w-10 rounded-full transition-all ${f.value ? 'bg-black dark:bg-white' : 'bg-black/[0.08] dark:bg-white/[0.1]'}`}
                  style={{ height: 22 }}
                >
                  <span className={`absolute top-0.5 rounded-full transition-all ${f.value ? 'left-[18px] bg-white dark:bg-black' : 'left-0.5 bg-white dark:bg-black'}`} style={{ width: 18, height: 18 }} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      <div className="flex items-center justify-between">
        <button onClick={handleSave} className="flex items-center gap-2 px-6 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity">
          <Save size={14} /> Save Settings
        </button>
        <button onClick={() => setShowResetConfirm(true)} className="flex items-center gap-2 px-5 py-3 border border-red-500/20 text-red-500 text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:bg-red-500/5 transition-all">
          <RotateCcw size={14} /> Reset to Defaults
        </button>
      </div>

      <AdminConfirmDialog isOpen={showResetConfirm} onClose={() => setShowResetConfirm(false)} onConfirm={handleReset} title="Reset to Defaults" message="This will reset ALL data including artworks, artists, exhibitions, art fairs, team members, orders, and settings to their original defaults. This action cannot be undone." />
    </div>
  );
};

export default AdminSettingsPage;
